import { useMemo } from 'react';
import { useProcessingDataExportDocument } from 'src/suitability-report/hooks/useProcessingDataExportDocument';
import { MeetingNoteMetadata } from 'src/meeting-note/types';

/** A list item: a summary whose list-guaranteed fields are required. Never carries the body. */
type MeetingNote = MeetingNoteMetadata &
  Required<
    Pick<MeetingNoteMetadata, 'id' | 'clientId' | 'createdAt' | 'noteType' | 'generatedFromAgenda'>
  > & {
    displayName: string;
    isGenerated: true;
  };

type Props = {
  clientId?: string;
};

export const useMeetingNotes = ({ clientId }: Props = {}) => {
  const { items, isLoading, isError, error, refetch } = useProcessingDataExportDocument<
    Omit<MeetingNote, 'displayName' | 'isGenerated'>
  >({
    clientId,
    resource: 'meeting-notes',
  });

  const meetingNotes = useMemo(
    () =>
      items.map(note => {
        const isProcessing = ['PENDING', 'PROCESSING'].includes(
          note.backgroundProcess?.status || '',
        );
        const hasFileName = Boolean(note.fileName);

        let displayName: string;

        if (hasFileName) {
          displayName = note.fileName!;
        } else if (isProcessing) {
          displayName = '';
        } else {
          displayName = note.noteType
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, char => char.toUpperCase());
        }

        return {
          ...note,
          isGenerated: true,
          displayName,
        };
      }),
    [items],
  );

  return {
    meetingNotes,
    isLoading,
    isError,
    error,
    refetch,
  };
};
